/*
 * Copyright (c) 2016 Omnigon Communications, LLC. All rights reserved.
 *
 * This software is the confidential and proprietary information of Omnigon Communications, LLC
 * ("Confidential Information"). You shall not disclose such Confidential Information and shall access and use it only
 * in accordance with the terms of the license agreement you entered into with Omnigon Communications, LLC, its
 * subsidiaries, affiliates or authorized licensee. Unless required by applicable law or agreed to in writing, this
 * Confidential Information is provided on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the license agreement for the specific language governing permissions and limitations.
 */

import BaseControl from 'scripts/base-control';
import can from 'can';
import dtm from 'scripts/services/dtm';
import stringUtil from 'scripts/utils/string';
import miniTeeTimesTableView from './mini-tee-times-table.stache';
import _ from 'lodash';

const updateInterval = 45 * 1000;

export const MiniTeeTimes = BaseControl.extend({
    init: function () {
        this._super();

        this.$container = this.$element.find('.js-tee-times-table');

        this.getData();
        setInterval(this.getData.bind(this), updateInterval);
    },

    getData() {
        can.ajax({
            type: 'GET',
            url: this.options.url,
            headers: {
                'Content-Type': 'application/json'
            },
            success: this.parseData.bind(this)
        });
    },

    getPlayerName(name) {
        const fullName = name.trim().replace('(a)', '').split(/\s+/);
        const isAmateur = name.indexOf('(a)') > -1;

        return `${stringUtil.makeShortPlayerName(fullName.join(' '))}${isAmateur ? ' (a)' : ''}`;
    },

    parseData(data) {
        const tournament = _.get(data, 'event.tournament');

        if (!tournament) {
            return;
        }

        const type = _.get(tournament, 'display.phase');

        const legends = _.get(tournament, `strokeplay.legends.legend`, _.get(tournament, `legends.legend`, []));
        const timezoneText = legends.type === 'times' ?
            legends.text :
            _.get(legends.find(legend => legend.type === 'times'), 'text', '');
        const isAmateur = tournament.name.toLowerCase().indexOf('open') > -1;

        let rows = [];

        switch (type) {
            case 'matchplay': {
                const matches = _.get(tournament, 'matchplay.matches.match', []);
                rows = (_.isArray(matches) ? matches : [matches]).map((match) => {
                    const teams = _.get(match, 'team', []).map(team => {
                        const names = _.get(team, 'player.name', '').split('/');
                        return names.map(this.getPlayerName).join(' / ');
                    }).join(' vs. ');
                    const players = _.get(match, 'players.player', [])
                        .map(player => this.getPlayerName(player.name)).join(' vs. ');
                    return {
                        tee: match.startingtee,
                        coursecode: match.coursecode,
                        time: match.teetime,
                        players: teams.length ? teams : players
                    }
                });
                break;
            }
            case 'strokeplay': {
                const players = _.get(tournament, 'strokeplay.players.player', []);
                rows = _(players)
                    .groupBy(team => `${team.matchnumber}-${team.course}`)
                    .map(teams => ({
                        tee: _.get(teams, '[0].startingtee', '--'),
                        coursecode: _.get(teams, '[0].coursecode', null),
                        time: _.get(teams, '[0].teetime', '--'),
                        players: teams.map((team) => {
                            const fullName = team.name.trim()
                                .replace('(a)', '')
                                .split(',').reverse().map(name => name.trim())
                                .join(' ').split(' ');
                            const firstName = fullName[0][0];
                            const lastName = fullName.slice(1).join(' ');
                            const isAmateur = team.name.indexOf('(a)') > -1;
                            return `${firstName}.&nbsp;${lastName}${isAmateur ? ' (a)' : ''}`;
                        }).join(', ')
                    }))
                    .value();
                break;
            }
        }

        this.$container.html(miniTeeTimesTableView({
            rows: rows.slice(0, 5),
            timezoneText,
            isAmateur
        }));
    },

    '.s-link click': function (el) {
        dtm.trackRightRailLinkToFullClick('MiniTeeTimes');
    }
});
